import { useState } from "react";
import {
  AlarmClock,
  Bell,
  CalendarDays,
  CalendarRange,
  ChevronDown,
  ClipboardCheck,
  Clock3,
  FileArchive,
  FileImage,
  FileText,
  FolderOpen,
  GraduationCap,
  ListTodo,
  MessageSquare,
  RefreshCw,
  Search,
  Trash2,
  Zap,
} from "lucide-react";
import { toast } from "sonner";
import { api } from "../api";
import {
  ActionButton,
  EmptyState,
  ErrorState,
  LoadingState,
  Pagination,
  Panel,
  SectionHeader,
  Tag,
} from "../components/design-system";
import { ConfirmDialog } from "../components/ui";
import { useResource } from "../hooks/use-resource";
import type { SnapshotOwner, SnapshotSummary, StorageObject } from "../types";
import { formatBytes, formatDateTime, resourceLabel } from "../utils";

const PAGE_SIZE = 6;

export function DataPage() {
  const [page, setPage] = useState(1);
  const [query, setQuery] = useState("");
  const [draftQuery, setDraftQuery] = useState("");
  const [resourceFilter, setResourceFilter] = useState("");
  const [expanded, setExpanded] = useState<string | null>(null);
  const [pendingDelete, setPendingDelete] = useState<{
    owner: SnapshotOwner;
    snapshot: SnapshotSummary;
  } | null>(null);
  const [deleteBusy, setDeleteBusy] = useState(false);
  const owners = useResource(
    () =>
      api.snapshotOwners({
        page,
        pageSize: PAGE_SIZE,
        q: query,
        resource: resourceFilter,
      }),
    [page, query, resourceFilter],
  );
  const resources = useResource(() => api.snapshotResources());
  const storage = useResource(() => api.storage());
  const calendars = useResource(() => api.calendars());

  const refreshAll = async () => {
    await Promise.all([
      owners.refresh(),
      resources.refresh(),
      storage.refresh(),
      calendars.refresh(),
    ]);
  };

  const deleteSnapshot = async () => {
    if (!pendingDelete) return;
    setDeleteBusy(true);
    try {
      await api.deleteSnapshot(
        pendingDelete.owner.userId,
        pendingDelete.snapshot.resourceKey,
      );
      toast.success(
        pendingDelete.snapshot.resourceKey === "electricity"
          ? "住宿绑定、电费数据和换绑冷却期已清除"
          : "持久快照已删除，下次查询时会重新获取",
      );
      setPendingDelete(null);
      await Promise.all([owners.refresh(), resources.refresh()]);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "删除快照失败");
    } finally {
      setDeleteBusy(false);
    }
  };

  const syncCalendar = async () => {
    try {
      const result = await api.runJob("sync-academic-calendars");
      toast.success(
        result.accepted ? "校历同步任务已启动" : "校历同步任务已经在运行",
      );
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "任务触发失败");
    }
  };

  const filter = (resourceKey: string) => {
    setPage(1);
    setExpanded(null);
    setResourceFilter(resourceFilter === resourceKey ? "" : resourceKey);
  };

  return (
    <div className="space-y-10">
      <section>
        <SectionHeader
          eyebrow="PERSISTED RESOURCES"
          title="用户持久化数据"
          desc="按用户聚合数据库快照 · 搜索定位后展开管理"
          className="rise-in"
          action={
            <ActionButton
              busy={
                owners.refreshing ||
                resources.refreshing ||
                storage.refreshing ||
                calendars.refreshing
              }
              onClick={() => void refreshAll()}
            >
              <RefreshCw size={13} />
              刷新全部
            </ActionButton>
          }
        />

        <div className="rise-in rise-in-1 mt-5 flex flex-wrap items-center gap-2">
          <form
            className="flex min-w-52 flex-1 items-center gap-2 rounded-full border border-sand-light bg-paper px-4 py-2 text-[13px] sm:max-w-xs"
            onSubmit={(event) => {
              event.preventDefault();
              setPage(1);
              setExpanded(null);
              setQuery(draftQuery.trim());
            }}
          >
            <Search size={14} className="shrink-0 text-ink-faint" />
            <input
              value={draftQuery}
              onChange={(event) => setDraftQuery(event.target.value)}
              placeholder="搜索用户姓名 / 学号…"
              className="w-full bg-transparent outline-none placeholder:text-ink-faint/70"
            />
          </form>
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => filter("")}
              className={`rounded-full border px-4 py-1.5 text-[12.5px] font-medium transition active:scale-95 ${!resourceFilter ? "border-ink bg-ink text-paper" : "border-sand-light bg-paper text-ink-soft hover:border-sand"}`}
            >
              全部
              <span className="ml-1.5 text-[10.5px] opacity-60">
                {resources.data?.reduce(
                  (sum, item) => sum + item.snapshotCount,
                  0,
                ) ?? "—"}
              </span>
            </button>
            {resources.data?.map((resource) => (
              <button
                key={resource.resourceKey}
                onClick={() => filter(resource.resourceKey)}
                className={`rounded-full border px-4 py-1.5 text-[12.5px] font-medium transition active:scale-95 ${resourceFilter === resource.resourceKey ? "border-ink bg-ink text-paper" : "border-sand-light bg-paper text-ink-soft hover:border-sand"}`}
              >
                {resourceLabel(resource.resourceKey)}
                <span className="ml-1.5 text-[10.5px] opacity-60">
                  {resource.snapshotCount}
                </span>
              </button>
            ))}
          </div>
        </div>

        {owners.error && (
          <div className="mt-4">
            <ErrorState
              error={owners.error}
              onRetry={() => void owners.refresh()}
            />
          </div>
        )}
        <Panel className="rise-in rise-in-2 mt-4 overflow-hidden">
          <div className="flex items-center justify-between border-b border-sand-light/70 bg-cream/50 px-5 py-2.5 text-[11.5px] text-ink-faint">
            <span>
              {owners.data
                ? `共 ${owners.data.pagination.total} 位用户持有匹配资源`
                : "正在读取持久资源"}
            </span>
            <span className="hidden sm:block">点击行展开资源明细</span>
          </div>
          {owners.loading && !owners.data ? (
            <LoadingState rows={7} />
          ) : owners.data?.items.length ? (
            <>
              <div className="divide-y divide-sand-light/60">
                {owners.data.items.map((owner) => {
                  const open = expanded === owner.userId;
                  return (
                    <div key={owner.userId}>
                      <button
                        onClick={() => setExpanded(open ? null : owner.userId)}
                        className={`flex w-full items-center gap-4 px-5 py-4 text-left transition ${open ? "bg-peach/50" : "hover:bg-cream/60"}`}
                      >
                        <div
                          className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full font-display text-[15px] ${open ? "bg-ink text-sand" : "bg-cream-deep text-ink-soft"}`}
                        >
                          {owner.displayName.slice(0, 1)}
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                            <p className="text-[14px] font-medium">
                              {owner.displayName}
                            </p>
                            <p className="font-mono text-[11.5px] text-ink-faint">
                              {owner.studentNumber || "—"}
                            </p>
                          </div>
                          <div className="mt-1.5 flex flex-wrap gap-1.5">
                            {owner.snapshots.map((snapshot) => (
                              <Tag
                                key={snapshot.resourceKey}
                                tone={resourceTone(snapshot.resourceKey)}
                              >
                                {resourceLabel(snapshot.resourceKey)}
                              </Tag>
                            ))}
                          </div>
                        </div>
                        <span className="hidden shrink-0 text-right text-[11.5px] text-ink-faint sm:block">
                          {formatBytes(owner.payloadBytes)}
                          <br />
                          更新 {formatDateTime(owner.latestAt, true)}
                        </span>
                        <ChevronDown
                          size={16}
                          className={`shrink-0 text-ink-faint transition-transform duration-300 ${open ? "rotate-180 text-clay" : ""}`}
                        />
                      </button>
                      {open && (
                        <div className="rise-in divide-y divide-dashed divide-sand-light/70 border-t border-sand-light/60 bg-cream/40">
                          {owner.snapshots.map((snapshot) => {
                            const ResourceIcon = resourceIcon(
                              snapshot.resourceKey,
                            );
                            return (
                              <div
                                key={snapshot.resourceKey}
                                className="flex flex-wrap items-center gap-3 py-3 pl-16 pr-5 sm:pl-[76px]"
                              >
                                <ResourceIcon
                                  size={15}
                                  strokeWidth={1.8}
                                  className="shrink-0 text-clay-deep"
                                />
                                <div className="min-w-0 flex-1">
                                  <p className="truncate text-[13px] text-ink">
                                    {resourceLabel(snapshot.resourceKey)} ·
                                    schema v{snapshot.schemaVersion}
                                  </p>
                                  <p className="mt-0.5 text-[11px] text-ink-faint">
                                    {formatBytes(snapshot.payloadBytes)} ·{" "}
                                    {formatDateTime(snapshot.fetchedAt)}
                                  </p>
                                </div>
                                <button
                                  onClick={() =>
                                    setPendingDelete({ owner, snapshot })
                                  }
                                  className="flex items-center gap-1 rounded-full border border-sand-light bg-paper px-3 py-1.5 text-[12px] font-medium text-ink-faint transition hover:border-[#E8C9B8] hover:text-destructive active:scale-95"
                                >
                                  <Trash2 size={12.5} />
                                  删除
                                </button>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
              <Pagination
                page={owners.data.pagination.page}
                pages={owners.data.pagination.totalPages}
                total={owners.data.pagination.total}
                onChange={(nextPage) => {
                  setPage(nextPage);
                  setExpanded(null);
                }}
                className="border-t border-sand-light/70 px-4 py-3.5"
              />
            </>
          ) : (
            <EmptyState
              title="没有匹配的用户或资源"
              description="调整筛选条件，或等待用户产生第一份持久快照。"
            />
          )}
        </Panel>
      </section>

      <section className="grid gap-4 lg:grid-cols-2">
        <div className="rise-in rise-in-2">
          <SectionHeader
            eyebrow="TEACHING CALENDAR"
            title="教学日历"
            desc="经定时任务同步至 MinIO 的最新校历"
            action={
              <ActionButton tone="ghost" onClick={() => void syncCalendar()}>
                <RefreshCw size={13} />
                检查更新
              </ActionButton>
            }
          />
          <Panel className="mt-5 flex h-[24rem] flex-col overflow-hidden">
            <div className="flex shrink-0 items-center gap-2 border-b border-sand-light/70 bg-peach/50 px-5 py-3 text-[12px] text-ink-soft">
              <CalendarDays size={13.5} className="text-clay-deep" />
              {storage.data?.bucket || "MinIO"} / calendars ·{" "}
              {calendars.data?.length ?? 0} 个对象
            </div>
            <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain">
              {calendars.error ? (
                <ErrorState
                  error={calendars.error}
                  onRetry={() => void calendars.refresh()}
                />
              ) : calendars.loading && !calendars.data ? (
                <LoadingState rows={4} />
              ) : calendars.data?.length ? (
                <div className="divide-y divide-sand-light/60">
                  {calendars.data.map((calendar) => (
                    <a
                      key={calendar.startYear}
                      href={calendar.sourcePageUrl}
                      target="_blank"
                      rel="noreferrer"
                      className="flex items-center gap-3.5 px-5 py-3.5 transition hover:bg-cream/60"
                    >
                      <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-peach text-clay-deep">
                        <FileImage size={15} />
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-[12.5px] font-medium text-ink">
                          {calendar.academicYear} 学年度校历
                        </p>
                        <p className="mt-0.5 text-[11px] text-ink-faint">
                          发布于 {calendar.publishedAt || "未知"} ·{" "}
                          {calendar.contentType}
                        </p>
                      </div>
                      <span className="font-serif-num text-[13px] text-ink-soft">
                        {formatBytes(calendar.size)}
                      </span>
                    </a>
                  ))}
                </div>
              ) : (
                <EmptyState
                  title="尚无教学日历"
                  description="运行同步任务后会存入 MinIO。"
                />
              )}
            </div>
          </Panel>
        </div>

        <div className="rise-in rise-in-3">
          <SectionHeader
            eyebrow="RECENT OBJECTS"
            title="最近对象"
            desc="MinIO 中最近写入的对象"
          />
          <Panel className="mt-5 flex h-[24rem] flex-col overflow-hidden">
            <div className="flex shrink-0 items-center gap-2 border-b border-sand-light/70 bg-peach/50 px-5 py-3 text-[12px] text-ink-soft">
              <Clock3 size={13.5} className="text-clay-deep" />
              {storage.data?.bucket || "对象存储"} · 按写入时间倒序
            </div>
            <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain">
              {storage.error ? (
                <ErrorState
                  error={storage.error}
                  onRetry={() => void storage.refresh()}
                />
              ) : storage.loading && !storage.data ? (
                <LoadingState rows={4} />
              ) : storage.data?.objects.length ? (
                <div className="divide-y divide-sand-light/60">
                  {storage.data.objects.slice(0, 6).map((object) => (
                    <ObjectRow
                      key={object.name}
                      object={object}
                      bucket={storage.data!.bucket}
                    />
                  ))}
                </div>
              ) : (
                <EmptyState
                  title="存储桶为空"
                  description="同步校历或上传公告图片后对象会出现在这里。"
                />
              )}
            </div>
          </Panel>
        </div>
      </section>

      <Panel className="rise-in rise-in-4 flex flex-wrap items-center gap-x-8 gap-y-3 px-6 py-5">
        <div className="flex items-center gap-2.5">
          <FolderOpen size={16} className="text-clay-deep" />
          <p className="text-[13px] font-medium">MinIO 存储</p>
        </div>
        <div className="h-2 min-w-40 flex-1 overflow-hidden rounded-full bg-cream-deep">
          <div className="h-full w-[35%] rounded-full bg-gradient-to-r from-sand to-clay" />
        </div>
        <p className="text-[12.5px] text-ink-soft">
          已存{" "}
          <span className="font-serif-num text-[14px] text-ink">
            {formatBytes(storage.data?.totalBytes)}
          </span>{" "}
          · {storage.data?.objectCount ?? "—"} 个对象 ·{" "}
          {storage.data?.bucket || "—"}
        </p>
      </Panel>

      <ConfirmDialog
        open={Boolean(pendingDelete)}
        title="删除这份持久快照？"
        description={
          pendingDelete?.snapshot.resourceKey === "electricity"
            ? `删除后不会影响用户账号；${pendingDelete.owner.displayName}的住宿绑定、电费数据和换绑冷却期都会清除，可立即重新绑定。`
            : `删除后不会影响用户账号；${pendingDelete?.owner.displayName || "该用户"}下次查询${resourceLabel(pendingDelete?.snapshot.resourceKey || "数据")}时将重新访问教务系统。`
        }
        confirmLabel="删除快照"
        danger
        busy={deleteBusy}
        onCancel={() => setPendingDelete(null)}
        onConfirm={() => void deleteSnapshot()}
      />
    </div>
  );
}

function resourceTone(resourceKey: string): "neutral" | "clay" | "sage" {
  if (resourceKey.includes("grade")) return "clay";
  if (
    resourceKey.includes("message") ||
    resourceKey.includes("notice") ||
    resourceKey.includes("schedule")
  ) {
    return "sage";
  }
  return "neutral";
}

function resourceIcon(resourceKey: string) {
  if (resourceKey.includes("grade")) return GraduationCap;
  if (resourceKey.includes("message")) return MessageSquare;
  if (resourceKey.includes("notice")) return Bell;
  if (resourceKey.includes("timetable")) return CalendarRange;
  if (resourceKey.startsWith("exams:")) return ClipboardCheck;
  if (resourceKey === "electricity") return Zap;
  if (resourceKey === "schedule") return ListTodo;
  return AlarmClock;
}

function ObjectRow({
  object,
  bucket,
}: {
  object: StorageObject;
  bucket: string;
}) {
  const ObjectIcon = object.name.match(/\.(png|jpe?g|webp|gif)$/i)
    ? FileImage
    : object.name.match(/\.(gz|zip)$/i)
      ? FileArchive
      : FileText;
  return (
    <div className="flex items-center gap-3.5 px-5 py-3.5 transition hover:bg-cream/60">
      <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-peach text-clay-deep">
        <ObjectIcon size={15} strokeWidth={1.8} />
      </div>
      <div className="min-w-0 flex-1">
        <p
          className="truncate font-mono text-[12px] font-medium text-ink"
          title={object.name}
        >
          {object.name}
        </p>
        <p className="mt-0.5 text-[11px] text-ink-faint">
          {bucket} · {formatDateTime(object.lastModified, true)}
        </p>
      </div>
      <span className="shrink-0 font-serif-num text-[13px] text-ink-soft">
        {formatBytes(object.size)}
      </span>
    </div>
  );
}
